import React from 'react';
import {
  Award, Building, Star, Calculator, Calendar, Compass, Crown, MessageSquare,
  ShieldCheck, Zap, FileCheck2, Users, HeartHandshake, TrendingUp,
  CheckCircle2, ArrowRight, Quote, Printer, Sparkles,
} from 'lucide-react';

const ICONS = {
  Award, Building, Star, Calculator, Calendar, Compass, Crown, MessageSquare,
  ShieldCheck, Zap, FileCheck2, Users, HeartHandshake, TrendingUp, Sparkles,
};

const fmtCompact = (v) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Math.round(v)}`;
};

const ReasonCard = ({ reason }) => {
  const Icon = ICONS[reason.icon] || Award;
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)] flex flex-col break-inside-avoid">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="w-11 h-11 rounded-[10px] bg-[#171717] flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-[#171717] font-heading leading-none">{reason.metric}</p>
          <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-semibold mt-1">{reason.metricLabel}</p>
        </div>
      </div>
      <h3 className="text-base font-semibold text-[#171717] font-heading">{reason.headline}</h3>
      <p className="text-xs text-[#525252] mt-2 leading-relaxed">{reason.copy}</p>
    </div>
  );
};

const DeliverableGroup = ({ group }) => {
  const Icon = ICONS[group.icon] || CheckCircle2;
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] break-inside-avoid">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
        <h4 className="text-sm font-semibold text-[#171717] font-heading uppercase tracking-wider">{group.group}</h4>
      </div>
      <p className="text-[11px] text-[#737373] italic mb-4">{group.tagline}</p>
      <ul className="space-y-2">
        {group.items.map((item) => (
          <li key={item} className="text-[12px] text-[#262626] flex items-start gap-2 leading-relaxed">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CommitmentChip = ({ commitment }) => {
  const Icon = ICONS[commitment.icon] || CheckCircle2;
  return (
    <div className="flex items-start gap-3 p-4 bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)] break-inside-avoid">
      <div className="w-9 h-9 rounded-[8px] bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-emerald-700" strokeWidth={1.75} />
      </div>
      <p className="text-[12px] text-[#171717] font-medium leading-relaxed pt-1.5">{commitment.label}</p>
    </div>
  );
};

const TransitionPhase = ({ phase, index, total }) => {
  const Icon = ICONS[phase.icon] || Compass;
  return (
    <div className="relative bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] break-inside-avoid">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#171717] text-white flex items-center justify-center font-mono text-xs font-bold">
          0{index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#A3A3A3]">{phase.phase}</p>
          <h4 className="text-sm font-semibold text-[#171717] font-heading flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
            {phase.title}
          </h4>
        </div>
      </div>
      <ul className="space-y-2">
        {phase.tasks.map((t) => (
          <li key={t} className="text-[11.5px] text-[#525252] flex items-start gap-2 leading-relaxed">
            <span className="text-[#171717] mt-1.5 w-1 h-1 rounded-full bg-[#171717] flex-shrink-0" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
      {index < total - 1 && (
        <ArrowRight className="hidden lg:block absolute -right-6 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" strokeWidth={1.5} />
      )}
    </div>
  );
};

const DifferentiatorRow = ({ row }) => (
  <div className="grid grid-cols-12 gap-3 items-stretch py-3 border-b border-[#F0F0F0] last:border-0 break-inside-avoid">
    <div className="col-span-12 lg:col-span-3 flex items-center">
      <p className="text-[11px] font-bold uppercase tracking-wider text-[#171717]">{row.concern}</p>
    </div>
    <div className="col-span-12 lg:col-span-4 p-3 bg-[#FAFAFA] border border-[#F0F0F0] rounded-[8px]">
      <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3] font-semibold mb-1">Incumbent</p>
      <p className="text-[11.5px] text-[#525252] leading-relaxed">{row.incumbent}</p>
    </div>
    <div className="col-span-12 lg:col-span-5 p-3 bg-emerald-50/40 border border-emerald-200 rounded-[8px]">
      <p className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">With us</p>
      <p className="text-[11.5px] text-[#171717] leading-relaxed font-medium">{row.us}</p>
    </div>
  </div>
);

const ProofQuote = ({ q }) => (
  <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] break-inside-avoid">
    <Quote className="w-4 h-4 text-[#A3A3A3] mb-2" strokeWidth={1.5} />
    <p className="text-[12.5px] text-[#171717] italic leading-relaxed">"{q.comment}"</p>
    <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F0F0F0]">
      <p className="text-[11px] font-semibold text-[#171717] truncate">{q.propertyName || `Plan ${q.planNumber}`}</p>
      <span className="text-[10px] font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
        NPS {q.score}/10
      </span>
    </div>
  </div>
);

const ProposalOverview = ({ overview }) => {
  if (!overview) return null;
  const p = overview;

  const handlePrint = () => {
    document.body.classList.add('print-proposal-only');
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove('print-proposal-only'), 500);
    }, 60);
  };

  return (
    <section id="proposal-overview" data-testid="proposal-overview" className="space-y-8 proposal-printable">
      {/* Hero / Promise */}
      <div className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white rounded-[16px] p-8 lg:p-12 shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />
        <div className="relative">
          <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 opacity-70" strokeWidth={1.5} />
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-70">Proposal Overview · For OC Committees</p>
            </div>
            <button
              type="button"
              onClick={handlePrint}
              data-testid="print-proposal-btn"
              className="no-print inline-flex items-center gap-2 bg-white text-[#0F172A] text-[11px] font-semibold uppercase tracking-wider px-4 py-2 rounded-full hover:bg-white/90 transition-colors"
            >
              <Printer className="w-3.5 h-3.5" strokeWidth={2} />
              Print Proposal
            </button>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold font-heading tracking-tight leading-tight max-w-4xl">
            {p.promise}
          </h2>
          <p className="text-sm lg:text-base text-white/70 leading-relaxed max-w-3xl mt-5">
            {p.subPromise}
          </p>

          {/* Proof metric strip */}
          <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div>
              <p className="text-2xl lg:text-3xl font-bold font-heading">{p.proofMetrics.lotsRepresented}</p>
              <p className="text-[10px] uppercase tracking-wider opacity-60 mt-1">active lots managed</p>
            </div>
            <div>
              <p className="text-2xl lg:text-3xl font-bold font-heading">{p.proofMetrics.groupsServed}</p>
              <p className="text-[10px] uppercase tracking-wider opacity-60 mt-1">OC plans</p>
            </div>
            <div>
              <p className="text-2xl lg:text-3xl font-bold font-heading">{p.proofMetrics.ratedSevenPlusPct}%</p>
              <p className="text-[10px] uppercase tracking-wider opacity-60 mt-1">rate us 7+ out of 10</p>
            </div>
            <div>
              <p className="text-2xl lg:text-3xl font-bold font-heading">97%</p>
              <p className="text-[10px] uppercase tracking-wider opacity-60 mt-1">OC manager retention</p>
            </div>
            <div>
              <p className="text-2xl lg:text-3xl font-bold font-heading">{p.proofMetrics.specialistManagers}</p>
              <p className="text-[10px] uppercase tracking-wider opacity-60 mt-1">specialist managers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Why we are selected */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Star className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
          <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">Why Committees Select Us</h3>
        </div>
        <p className="text-xs text-[#737373] mb-5 max-w-3xl leading-relaxed">
          Five reasons grounded in the data — not marketing language. Every number is drawn from the live portfolio.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {p.reasons.map((r) => <ReasonCard key={r.key} reason={r} />)}
        </div>
      </div>

      {/* What's included */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
          <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">What's Included · Service Scope</h3>
        </div>
        <p className="text-xs text-[#737373] mb-5 max-w-3xl leading-relaxed">
          Mapped to the five priorities of every owners corporation committee. No surprises in scope — these are inclusions, not "optional extras".
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {p.deliverables.map((d) => <DeliverableGroup key={d.group} group={d} />)}
        </div>
      </div>

      {/* Our commitments */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <HeartHandshake className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
          <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">Our Commitments</h3>
        </div>
        <p className="text-xs text-[#737373] mb-5 max-w-3xl leading-relaxed">
          Service-level commitments that travel with every engagement — measured, reviewed, and reported back to the committee.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {p.commitments.map((c) => <CommitmentChip key={c.label} commitment={c} />)}
        </div>
      </div>

      {/* Differentiators */}
      <div className="bg-white border border-[#E5E5E5] rounded-[12px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="flex items-center gap-2 mb-1">
          <Compass className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
          <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">What Changes When You Switch</h3>
        </div>
        <p className="text-xs text-[#737373] mb-5 max-w-3xl leading-relaxed">
          The committee concerns we hear most often during proposal conversations — and how the experience changes once we hold the file.
        </p>
        <div className="divide-y divide-[#F0F0F0]">
          {p.differentiators.map((d) => <DifferentiatorRow key={d.concern} row={d} />)}
        </div>
      </div>

      {/* Transition Plan */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Calendar className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
          <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">90-Day Transition Plan</h3>
        </div>
        <p className="text-xs text-[#737373] mb-5 max-w-3xl leading-relaxed">
          A structured handover from your current manager. Phased so the committee sees progress every 30 days — and so nothing operational drops in the gap.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
          {p.transition.map((phase, i) => (
            <TransitionPhase key={phase.phase} phase={phase} index={i} total={p.transition.length} />
          ))}
        </div>
      </div>

      {/* Proof in their words */}
      {p.proofQuotes && p.proofQuotes.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Quote className="w-4 h-4 text-[#171717]" strokeWidth={1.5} />
            <h3 className="text-base font-semibold uppercase tracking-wider text-[#171717]">In Their Words</h3>
          </div>
          <p className="text-xs text-[#737373] mb-5 max-w-3xl leading-relaxed">
            Hand-picked from {p.proofMetrics.verbatimTestimonials} verbatim committee testimonials. Reference clients available on request.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {p.proofQuotes.map((q) => <ProofQuote key={q.planNumber + q.comment.slice(0, 20)} q={q} />)}
          </div>
        </div>
      )}

      {/* Closing CTA */}
      <div className="bg-[#171717] text-white rounded-[16px] p-8 lg:p-10 shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-60 mb-2">Next Step</p>
            <h3 className="text-2xl lg:text-3xl font-bold font-heading tracking-tight">
              A 30-minute committee briefing — tailored to your building.
            </h3>
            <p className="text-sm text-white/70 mt-3 leading-relaxed">
              We'll walk through the proposal in person, address any specific concerns the committee has raised, and provide reference contacts from comparable OCs in our portfolio.
            </p>
          </div>
          <div className="flex flex-col gap-2 lg:items-end">
            <div className="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#171717] rounded-full font-semibold text-sm">
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
              Reference clients available on request
            </div>
            <p className="text-[10px] uppercase tracking-wider opacity-50">
              Insurance · ASIC · Strata Community Australia member
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProposalOverview;
